/**
 * Automações — listagem + galeria de modelos (issue #137).
 *
 * Fluxos no modelo do n8n (gatilho → condição → ação), editados em
 * /automacoes/:id. Aqui o usuário vê as automações do cliente selecionado,
 * liga/desliga cada uma e cria novas a partir de um modelo pronto (galeria
 * inspirada no GERU) ou do zero.
 *
 * Escopo: organização + cliente, igual ao resto do CRM — a automação é
 * compartilhada na organização e só dispara com eventos do cliente dela.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { autCatalog, autCreate, autDelete, autList, autUpdate } from '../../db/api.js'
import AutomacaoTemplates from './AutomacaoTemplates.jsx'
import { RUN_STATUS } from './AutomacaoExecucoes.jsx'
import './automacoes.css'

const TRIGGER_LABEL = {
  lead_created: 'Lead criado',
  deal_stage_changed: 'Negócio mudou de etapa',
  whatsapp_message_received: 'WhatsApp recebido',
}

function tempoRelativo(iso) {
  if (!iso) return null
  const min = Math.round((Date.now() - new Date(iso)) / 60000)
  if (min < 1) return 'agora'
  if (min < 60) return `há ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `há ${h} h`
  return `há ${Math.round(h / 24)} d`
}

export default function Automacoes() {
  const navigate = useNavigate()
  const { selectedClient, toast } = useApp()
  const clientId = selectedClient?.id || null

  const [items, setItems] = useState([])
  const [catalog, setCatalog] = useState({ nodes: [], templates: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [view, setView] = useState('lista')          // lista | modelos
  const [busyKey, setBusyKey] = useState(null)
  const [toggling, setToggling] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const catalogByKey = useMemo(
    () => Object.fromEntries(catalog.nodes.map((n) => [n.key, n])),
    [catalog.nodes],
  )

  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return }
    setLoading(true)
    try {
      const [list, cat] = await Promise.all([autList(), autCatalog()])
      setItems(list)
      setCatalog(cat)
      setError(null)
      if (!list.length) setView('modelos')
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => { load() }, [load])

  const criar = async ({ nome, templateKey = null }) => {
    setBusyKey(templateKey || '__blank')
    try {
      const row = await autCreate({ nome, templateKey })
      navigate(`/automacoes/${row.id}`)
    } catch (e) {
      toast?.(`Não foi possível criar: ${e.message}`, 'error')
      setBusyKey(null)
    }
  }

  const alternarAtivo = async (item) => {
    setToggling(item.id)
    try {
      const row = await autUpdate(item.id, { ativo: !item.ativo })
      setItems((cur) => cur.map((i) => (i.id === item.id ? { ...i, ...row } : i)))
      toast?.(row.ativo ? 'Automação ativada' : 'Automação desativada')
    } catch (e) {
      const erros = e.detail?.errors
      toast?.(erros?.length ? `${e.message} ${erros[0]}` : e.message, 'error')
    } finally {
      setToggling(null)
    }
  }

  const excluir = async () => {
    const item = confirmDelete
    setConfirmDelete(null)
    try {
      await autDelete(item.id)
      setItems((cur) => cur.filter((i) => i.id !== item.id))
      toast?.('Automação excluída')
    } catch (e) {
      toast?.(e.message, 'error')
    }
  }

  // O título do módulo já está na topbar (Layout); o topo da página só tem a
  // troca de visão, e só quando já existe automação.
  const header = items.length > 0 && (
    <div className="aut-page-head">
      <div className="aut-seg" role="tablist" aria-label="Visão">
        <button type="button" role="tab" aria-selected={view === 'lista'}
          className={view === 'lista' ? 'is-active' : ''} onClick={() => setView('lista')}>
          Minhas automações <span className="aut-seg-count">{items.length}</span>
        </button>
        <button type="button" role="tab" aria-selected={view === 'modelos'}
          className={view === 'modelos' ? 'is-active' : ''} onClick={() => setView('modelos')}>
          Modelos
        </button>
      </div>
    </div>
  )

  let body
  if (!clientId) {
    body = <div className="aut-empty">Selecione um cliente para ver as automações.</div>
  } else if (loading) {
    body = <div className="aut-empty">Carregando…</div>
  } else if (error) {
    body = <div className="aut-empty aut-error-text">{error}</div>
  } else if (view === 'modelos') {
    body = (
      <>
        {!items.length && (
          <div className="aut-hero">
            <span className="aut-hero-icon"><Icon n="zap" s={22} /></span>
            <div>
              <strong>Comece por um modelo pronto</strong>
              <span>Escolha um fluxo, ajuste os detalhes no editor e ative. Leva menos de um minuto.</span>
            </div>
          </div>
        )}
        <AutomacaoTemplates
          templates={catalog.templates} catalog={catalogByKey} busyKey={busyKey}
          onUse={(t) => criar({ nome: t.nome, templateKey: t.key })}
          onBlank={() => criar({ nome: 'Nova automação' })}
        />
      </>
    )
  } else {
    body = (
      <div className="aut-list">
        {items.map((item) => {
          const st = item.last_run_status ? RUN_STATUS[item.last_run_status] : null
          return (
            <div key={item.id} className="aut-row">
              <button type="button" className="aut-row-main" onClick={() => navigate(`/automacoes/${item.id}`)}>
                <span className={`aut-row-icon${item.ativo ? ' is-on' : ''}`}><Icon n="zap" s={16} /></span>
                <span className="aut-row-text">
                  <strong>{item.nome}</strong>
                  <span>
                    {TRIGGER_LABEL[item.trigger_type] || 'Sem gatilho'}
                    {item.descricao ? ` · ${item.descricao}` : ''}
                  </span>
                </span>
              </button>
              <div className="aut-row-stats">
                {st ? (
                  <span className="aut-row-last">
                    <span className={`badge ${st.badge}`}>{st.label}</span>
                    {tempoRelativo(item.last_run_at)}
                  </span>
                ) : <span className="aut-row-last aut-muted">Nunca executou</span>}
                <span className="aut-muted">
                  {item.runs_30d} execuç{item.runs_30d === 1 ? 'ão' : 'ões'} em 30 dias
                  {item.errors_30d > 0 && <span className="aut-error-text"> · {item.errors_30d} com erro</span>}
                </span>
              </div>
              <div className="aut-row-actions">
                <label className="aut-switch" title={item.errors?.length ? item.errors[0] : undefined}>
                  <input
                    type="checkbox" checked={!!item.ativo}
                    disabled={toggling === item.id || (!item.ativo && item.errors?.length > 0)}
                    onChange={() => alternarAtivo(item)}
                    aria-label={item.ativo ? 'Desativar automação' : 'Ativar automação'}
                  />
                  <span />
                </label>
                <button type="button" className="btn-icon" aria-label="Excluir automação"
                  onClick={() => setConfirmDelete(item)}>
                  <Icon n="trash" s={15} />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Layout title="Automações" breadcrumb={[{ label: 'Automações' }]}>
      <div className="aut-page">
        {header}
        {body}
      </div>

      {confirmDelete && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setConfirmDelete(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 className="modal-title">Excluir automação?</h2></div>
            <div className="modal-body">
              <p className="modal-message">
                “{confirmDelete.nome}” para de rodar na hora, inclusive execuções aguardando uma espera.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setConfirmDelete(null)}>Cancelar</button>
              <button type="button" className="btn-red btn-sm" onClick={excluir}>Excluir</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
